import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Chip,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AppModal from "../../../../components/AppModal/AppModal";
import { confirmDialog } from "../../../../components/ConfirmDialog/confirmDialog";
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { useAuth } from '../../../../auth/auth';
import {
  createAnnualLeave,
  deleteAnnualLeave,
  getAnnualLeavesById,
} from '../../../../api/apiCall';
import EditHolidayForm from './EditHolidayForm';

interface Booking {
  id?: string;
  userId: string;
  startDate: string;
  endDate: string;
  notes?: string;
}

const computeStatus = (start: string, end: string): 'upcoming' | 'ongoing' | 'past' => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (endDate < today) return 'past';
  if (startDate <= today && endDate >= today) return 'ongoing';
  return 'upcoming';
};

const StatusChip = ({ status }: { status: 'upcoming' | 'ongoing' | 'past' }) => {
  const colorMap = {
    upcoming: { label: 'Upcoming', color: 'info' as const },
    ongoing: { label: 'Ongoing', color: 'success' as const },
    past: { label: 'Past', color: 'default' as const },
  };
  return <Chip label={colorMap[status].label} color={colorMap[status].color} size="small" />;
};

const Holiday = ({ profileInfo }: any) => {
  const { state } = useAuth();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newBooking, setNewBooking] = useState({
    startDate: dayjs(),
    endDate: dayjs().add(1, 'day'),
    notes: '',
  });

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingBooking, setEditingBooking] = useState<Booking | null>(null);

  const getAnnualLeaves = async () => {
    setLoading(true);
    try {
      const response = await getAnnualLeavesById(state.user?.token, profileInfo?.id);
      setBookings(response.data || []);
    } catch (error) {
      console.error('Failed to fetch annual leaves:', error);
      toast.error('Error fetching holidays');
    }
    setLoading(false);
  };

  useEffect(() => {
    getAnnualLeaves();
  }, []);

  const handleAddBooking = async () => {
    const { startDate, endDate, notes } = newBooking;
    if (!startDate || !endDate) {
      toast.warning('Please fill in both Start Date and End Date.');
      return;
    }

    const payload: Booking = {
      userId: profileInfo?.id,
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD'),
      notes: notes?.trim() || undefined,
    };

    try {
      await createAnnualLeave(state.user?.token, payload);
      toast.success('Holiday added successfully');
      setIsAddModalOpen(false);
      getAnnualLeaves();
      setNewBooking({
        startDate: dayjs(),
        endDate: dayjs().add(1, 'day'),
        notes: '',
      });
    } catch (error) {
      console.error('Failed to create annual leave:', error);
      toast.error('Error creating booking');
    }
  };

  const handleDelete = async (id: string) => {
    confirmDialog({
      title: 'Are you sure you want to delete this holiday?',
      onOk: async () => {
        try {
          await deleteAnnualLeave(state.user?.token, id);
          toast.success('Holiday deleted');
          getAnnualLeaves();
        } catch (error) {
          console.error('Failed to delete annual leave:', error);
          toast.error('Error deleting booking');
        }
      },
      okButtonProps: { danger: true },
      okText: 'YES',
      cancelText: 'NO',
      centered: true,
    });
  };

  const openEditModal = (booking: Booking) => {
    setEditingBooking(booking);
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditingBooking(null);
  };

  const columns = [
    {
      field: 'startDate',
      headerName: 'Start Date',
      flex: 1,
      renderCell: (params: any) => (
        <div>{dayjs(params.value).format('DD/MM/YYYY')}</div>
      ),
    },
    {
      field: 'endDate',
      headerName: 'End Date',
      flex: 1,
      renderCell: (params: any) => (
        <div>{dayjs(params.value).format('DD/MM/YYYY')}</div>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      renderCell: (params: any) => {
        const status = computeStatus(params.row.startDate, params.row.endDate);
        return <StatusChip status={status} />;
      },
    },
    {
      field: 'notes',
      headerName: 'Notes',
      flex: 1.5,
      renderCell: (params: any) => <div>{params.value || '—'}</div>,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      sortable: false,
      renderCell: (params: any) => (
        <>
          <Button
            variant="contained"
            size="small"
            style={{ backgroundColor: 'gray', boxShadow: 'none', marginRight: 8 }}
            onClick={() => openEditModal(params.row)}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            style={{ boxShadow: 'none' }}
            onClick={() => handleDelete(params.row.id)}
          >
            Delete
          </Button>
        </>
      ),
    },
  ];

  const filteredBookings = bookings.filter((b) =>
    (b.notes || '').toLowerCase().includes(searchTerm.toLowerCase().trim())
  );

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <h2>Holidays</h2>
        <Box display="flex" gap={2}>
          <TextField
            placeholder="Search notes..."
            variant="outlined"
            size="small"
            style={{ width: 250 }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={() => setIsAddModalOpen(true)}
            style={{ boxShadow: 'none', width: 160 }}
          >
            Add Holiday
          </Button>
        </Box>
      </Box>

      <Box sx={{ height: 474, width: '100%' }}>
        <DataGrid
          rows={filteredBookings}
          columns={columns}
          disableRowSelectionOnClick
          loading={loading}
          sx={{
            '& .MuiDataGrid-columnHeaderTitle': {
              color: '#333',
              fontWeight: 'bold',
              fontSize: 15.5,
            },
          }}
        />
      </Box>

      <AppModal
        title={`Add Holiday for ${profileInfo?.name} ${profileInfo?.surname}`}
        open={isAddModalOpen}
        onCancel={() => setIsAddModalOpen(false)}
        footer={[
          <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <Button variant="contained" sx={{ boxShadow: 'none', mb: 2 }} onClick={handleAddBooking}>
              Add Holiday
            </Button>
          </div>,
        ]}
      >
        <Box display="grid" gridTemplateColumns="1fr" gap={3}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DemoContainer components={['DatePicker', 'DatePicker']}>
              <DatePicker
                label="Start Date"
                value={newBooking.startDate}
                onChange={(newValue) =>
                  setNewBooking((prev) => ({ ...prev, startDate: newValue || dayjs() }))
                }
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                format="DD/MM/YYYY"
              />
              <DatePicker
                label="End Date"
                value={newBooking.endDate}
                onChange={(newValue) =>
                  setNewBooking((prev) => ({ ...prev, endDate: newValue || dayjs().add(1, 'day') }))
                }
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                format="DD/MM/YYYY"
              />
            </DemoContainer>
          </LocalizationProvider>
          <TextField
            label="Notes (optional)"
            variant="outlined"
            size="small"
            fullWidth
            value={newBooking.notes}
            onChange={(e) =>
              setNewBooking((prev) => ({ ...prev, notes: e.target.value }))
            }
          />
        </Box>
      </AppModal>

      <EditHolidayForm
        open={isEditModalOpen}
        onClose={closeEditModal}
        booking={editingBooking}
        profileInfo={profileInfo}
        getAnnualLeaves={getAnnualLeaves}
      />
    </>
  );
}

export default Holiday;
